import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { UserRole } from '../../../../../types';
import { useForm } from 'react-hook-form';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { UserKeys, updateRole } from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import AvatarChip from '../../../../shared/avatar-chip';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface RoleDirectorySearchFormProps {
  changeLogRoles: string[];
  role: UserRole;
}

export default function RoleDirectorySearchForm({
  changeLogRoles,
  role,
}: RoleDirectorySearchFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const autocompleteKeyOrgs = useRef(new Date().getTime().toString());
  const [autoCompleteResponseOrgs, setAutoCompleteResponseOrgs] = useState('');
  const queryClient = useQueryClient();

  const { mutate, isPending: updatingRole } = useMutation({
    mutationFn: (data: UserRole) => updateRole(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Role directory search settings'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) {
          return;
        } else {
          const filteredList = oldData.filter(
            (currentRole) => currentRole.roleName !== role.roleName
          );
          return [...filteredList, data];
        }
      });
      reset();
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the role directory search settings'
        ),
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(
        analytics.administration.role_details.update_role_directory_search,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections.roleManager,
          roleType: data.roleType,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isLoading },
    getValues,
    setValue,
    watch,
    reset,
  } = useForm<UserRole>({
    values: {
      ...role,
      dirsearchBlockListOrgs: role.dirsearchBlockListOrgs
        ? role.dirsearchBlockListOrgs
        : [],
      dirsearchBlockListGroups: role.dirsearchBlockListGroups
        ? role.dirsearchBlockListGroups
        : [],
    },
  });

  const onSubmit = (data: UserRole) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, AdministrationRoles.EditRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const dirsearchBlockListOrgs = watch('dirsearchBlockListOrgs') || [];
  // const dirsearchBlockListGroups = watch('dirsearchBlockListGroups') || [];

  return (
    <Box
      component="form"
      sx={{ position: 'relative', height: '100%' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{ position: 'absolute', top: -50, right: -15 }}
      >
        <Button
          variant="contained"
          disabled={!isDirty || updatingRole}
          loading={updatingRole || isLoading}
          sx={{ position: 'absolute', top: 0, right: 0 }}
          type="submit"
        >
          Save
        </Button>
      </Stack>

      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 300 }}>Orgs</Typography>
        <Stack
          sx={{
            pt: 1,
            px: 4,
          }}
          gap={1}
        >
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            Optionally select any org units whose users you would like to
            exclude from directory search.
          </Typography>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ width: 400 }}>
              <OrganizationsAutoComplete
                newKey={autocompleteKeyOrgs.current}
                autoCompleteValue={autoCompleteResponseOrgs}
                setAutoCompleteValue={setAutoCompleteResponseOrgs}
                onSelection={(value) => {
                  if (!value || dirsearchBlockListOrgs?.includes(value)) return;
                  if (dirsearchBlockListOrgs) {
                    setValue(
                      'dirsearchBlockListOrgs',
                      [...dirsearchBlockListOrgs!, value],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else {
                    setValue('dirsearchBlockListOrgs', [value], {
                      shouldDirty: true,
                    });
                  }
                  autocompleteKeyOrgs.current = new Date().getTime().toString();
                  setAutoCompleteResponseOrgs('');
                }}
              />
            </Box>
          </Stack>
          {dirsearchBlockListOrgs && dirsearchBlockListOrgs.length > 0 ? (
            <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {dirsearchBlockListOrgs.map((org) => {
                return (
                  <AvatarChip
                    key={org}
                    label={org}
                    type={'OrgUnit'}
                    email={org}
                    onDelete={() => {
                      setValue(
                        'dirsearchBlockListOrgs',
                        dirsearchBlockListOrgs.filter(
                          (blockedOrg) => blockedOrg !== org
                        ),
                        {
                          shouldDirty: true,
                        }
                      );
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newRole = getValues();
          mutate(newRole);
        }}
        mutateLoading={updatingRole}
      />
    </Box>
  );
}
